import React, { useState, useRef, useEffect } from "react";
import {
  Bell,
  Wallet,
  User,
  ChevronUp,
  ChevronDown,
  Check,
  X,
  Clock,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { db, messaging } from "../../config/firebase";
import { ref, update, get, getDatabase } from "firebase/database"; // Import RTDB

import { useAppState } from "../../hooks/useAppState";
import { transferFunds } from "../../services/walletService";
import {
  markNotificationAsRead,
  markGlobalNotificationAsRead,
  markAllNotificationsAsRead,
  markAllGlobalNotificationsAsRead,
  selectUserNotifications,
  selectAllNotifications,
} from "../../store/slices/notificationSlice";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { app } from "../../config/firebase";
const rtdb = getDatabase(app); // Initialize RTDB

const NavbarNotification: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppState();
  const notificationsRef = useRef<HTMLDivElement>(null);
  // Notification states
  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);
  const [fundRequestOpen, setFundRequestOpen] = useState(false);
  const [supportyRequestOpen, setSupportyRequestOpen] = useState(false);
  const [showAllFundRequests, setShowAllFundRequests] = useState(false);
  const [showAllSupportyRequests, setShowAllSupportyRequests] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Track displayed notifications to prevent duplicates
  const [displayedNotificationIds, setDisplayedNotificationIds] = useState<
    string[]
  >([]);
  // Track time left for each support request notification
  const [supportRequestTimers, setSupportRequestTimers] = useState<{
    [id: string]: number;
  }>({});

  // Profile data
  const profileData = useAppSelector((state) => state.profile.user);
  const expertProfile = useAppSelector((state) => state.profile.expertProfile);
  const customerProfile = useAppSelector(
    (state) => state.profile.customerProfile
  );
  const profileName =
    profileData?.name ||
    (user?.role === "Expert" ? expertProfile?.name : customerProfile?.name) ||
    user?.name;
  // Get notifications from Redux store
  const userNotifications = useAppSelector(selectUserNotifications);
  const allNotifications = useAppSelector(selectAllNotifications);

  // Track previous notification count for detecting new notifications
  const [prevNotificationCount, setPrevNotificationCount] = useState(0);
  async function requestPermission() {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      // Generate Token
      const token = await getToken(messaging, {
        vapidKey:
          "BNWMA2MBanGpCS3zQJqh4meZHjBPBOJjYjAwd1hg2JRMiVYIoERSrCj-h0h9L5ytlDL0r3j6FGENAOdvBcnHUAo",
      });
      console.log("Token Gen", token); // Save token to Firestore in DeviceToken collection
      if (token && user?.uid) {
        try {
          // Reference to the user's device token document in the correct collection
          // Changed from "DeviceToken" to "deviceTokens" to match the collection used in initializeFCM
          const deviceTokenRef = doc(db, "deviceTokens", user.uid);

          // Save the token with additional device information
          await setDoc(
            deviceTokenRef,
            {
              token,
              userId: user.uid,
              device: "web",
              platform: navigator.platform,
              browser: navigator.userAgent,
              lastUpdated: new Date().toISOString(),
            },
            { merge: true }
          );

          console.log("Device token saved successfully for user:", user.uid);
        } catch (error) {
          console.error("Error saving device token:", error);
        }
      }
    }
  }

  useEffect(() => {
    // Req user for notification permission
    requestPermission();
  }, []);

  // Display appropriate notifications based on user role
  const displayedNotifications =
    user?.role === "supporty_admin" ? allNotifications : userNotifications;
  // Filter fund request notifications
  const fundRequestNotifications = displayedNotifications.filter(
    (notification) =>
      notification.trigger === "fund_request" ||
      notification.trigger === "fund_request_send" ||
      notification.trigger === "fund_request_receive" ||
      notification.trigger === "fund_request_approved" ||
      notification.trigger === "fund_request_rejected" ||
      notification.trigger === "funds_distributed" ||
      notification.trigger === "funds_received" ||
      notification.trigger === "funds_added"
  );

  // Filter supporty request notifications
  const supportyRequestNotifications = displayedNotifications.filter(
    (notification) =>
      notification.trigger === "new_support_request" ||
      notification.trigger === "support_request_submitted" ||
      notification.trigger === "support_request_accepted" ||
      notification.trigger === "support_request_rejected" ||
      notification.trigger === "support_request_missed"
  );

  // Handle clicks outside the notifications dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setShowNotificationsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Navigation handler
  const handleNavigation = (path: string): void => {
    // Navigate to role-specific notification page based on user role
    if (user?.role === "Expert") {
      navigate("/expert/notification");
    } else if (user?.role === "Customer") {
      navigate("/user/notification");
    } else if (user?.role === "supporty_admin") {
      navigate("/admin/notification");
    } else {
      // Default fallback path
      navigate("/user/notification");
    }
  };

  useEffect(() => {
    // Skip if no user is logged in
    if (!user?.uid) return;

    // Get all unread notifications
    const unreadNotifications = displayedNotifications.filter((n) => !n.read);

    // Find notifications that haven't been displayed as browser notifications yet
    const newNotifications = unreadNotifications.filter(
      (notification) =>
        notification.id && !displayedNotificationIds.includes(notification.id)
    );

    if (newNotifications.length > 0) {
      console.log(`New notifications detected: ${newNotifications.length}`);

      // Add these notification IDs to our tracked list to prevent duplicates
      const newIds = newNotifications
        .map((notification) => notification.id)
        .filter((id): id is string => !!id);

      setDisplayedNotificationIds((prevIds) => [...prevIds, ...newIds]);

      // Send browser notification ONLY for fund requests and support requests
      newNotifications.forEach(async (notification) => {
        // For new fund requests
        if (notification.trigger === "fund_request") {
          const title = "New Fund Request";
          const body = notification.message || "You have a new fund request";

          // Send push notification
          await sendPushNotification(user.uid, title, body, {
            notificationType: "fund_request",
            notificationId: notification.id,
            ...notification.meta,
          });
        } else {
          // Send push notification for ALL other notification types
          const title = notification.title || "New Notification";
          const body = notification.message || "You have a new notification";

          await sendPushNotification(user.uid, title, body, {
            notificationType: notification.trigger || "other",
            notificationId: notification.id,
            ...notification.meta,
          });
        }
        // No push notifications for other notification types
      });
    }

    // Still maintain previous count for compatibility
    setPrevNotificationCount(unreadNotifications.length);
  }, [displayedNotifications, displayedNotificationIds, user?.uid]);
  // Initialize FCM and manage token when user logs in
  useEffect(() => {
    if (!user?.uid) return;

    // Function to initialize FCM
    const initializeFCM = async () => {
      try {
        // Request permission
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.log("Notification permission denied");
          return;
        }

        // Register service worker (for web portal context)
        let swRegistration;
        if ("serviceWorker" in navigator) {
          try {
            swRegistration = await navigator.serviceWorker.register(
              "/firebase-messaging-sw.js"
            );
            console.log(
              "Service Worker registered with scope:",
              swRegistration.scope
            );
          } catch (error) {
            console.error("Service Worker registration failed:", error);
          }
        }

        // Get FCM token
        const messaging = getMessaging(app);
        const vapidKey =
          "BNWMA2MBanGpCS3zQJqh4meZHjBPBOJjYjAwd1hg2JRMiVYIoERSrCj-h0h9L5ytlDL0r3j6FGENAOdvBcnHUAo";

        try {
          const fcmToken = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: swRegistration,
          });

          if (fcmToken) {
            console.log("FCM token retrieved:", fcmToken);

            // Store token in Firestore - create the document first if it doesn't exist
            const tokenRef = doc(db, "deviceTokens", user.uid);

            try {
              // Check if document exists
              const tokenDoc = await getDoc(tokenRef);

              if (!tokenDoc.exists()) {
                // Create new document if it doesn't exist
                await setDoc(tokenRef, {
                  token: fcmToken,
                  device: "browser",
                  platform: navigator.platform,
                  browser: navigator.userAgent,
                  lastUpdated: new Date().toISOString(),
                  userId: user.uid,
                  createdAt: new Date().toISOString(),
                });
              } else {
                // Update existing document
                await updateDoc(tokenRef, {
                  token: fcmToken,
                  device: "browser",
                  platform: navigator.platform,
                  browser: navigator.userAgent,
                  lastUpdated: new Date().toISOString(),
                  userId: user.uid,
                });
              }

              console.log("FCM token stored successfully for user:", user.uid);
            } catch (dbError) {
              console.error("Error storing FCM token in Firestore:", dbError);
            } // Listen for foreground messages
            onMessage(messaging, (payload) => {
              console.log("Foreground message received:", payload);

              // Create notification manually for foreground messages
              if (
                Notification.permission === "granted" &&
                payload.notification
              ) {
                const notificationOptions = {
                  body: payload.notification.body,
                  icon: "/logo192.png",
                };

                // Add "Supporty Desk" to the title
                const notificationTitle =
                  "New service request: " +
                  (payload.notification.title || "New Notification");

                const notification = new Notification(
                  notificationTitle,
                  notificationOptions
                );

                notification.onclick = () => {
                  console.log("Notification clicked");
                  window.focus();
                  notification.close();

                  // Handle navigation based on notification type and user role
                  if (
                    payload.data?.notificationType === "fund_request" ||
                    payload.data?.notificationType === "support_request"
                  ) {
                    if (user?.role === "Expert") {
                      navigate("/expert/notification");
                    } else if (user?.role === "Customer") {
                      navigate("/user/notification");
                    } else if (user?.role === "supporty_admin") {
                      navigate("/admin/notification");
                    } else {
                      navigate("/user/notification");
                    }
                  }
                };
              }
            });
          } else {
            console.error("No FCM token received");
          }
        } catch (error) {
          console.error("Error initializing FCM:", error);
        }
      } catch (error) {
        console.error("Error initializing FCM:", error);
      }
    };

    initializeFCM();
  }, [user?.uid, navigate]);

  // Define the sendPushNotification function
  const sendPushNotification = async (
    userId: string,
    title: string,
    body: string,
    data: any = {}
  ) => {
    try {
      // Check if browser notifications are supported and permission is granted
      if (!("Notification" in window)) {
        console.log("Browser does not support notifications");
        return false;
      }

      // If permission is not granted, we won't show notifications
      if (Notification.permission !== "granted") {
        console.log("Notification permission not granted");
        return false;
      } // Create and show the browser notification
      const notificationOptions = {
        body: body,
        icon: "/logo192.png", // Use your app's icon
        tag: data.notificationId || `notification-${Date.now()}`, // Use ID as tag to prevent duplicates
        data: data,
      };

      // Add "Supporty Desk" to the title
      const notificationTitle = "Supporty: " + title;

      // Create the notification
      const notification = new Notification(
        notificationTitle,
        notificationOptions
      );

      // Handle notification click
      notification.onclick = () => {
        console.log("Notification clicked", data);
        window.focus();
        notification.close();

        // Navigate based on notification type
        if (data.notificationType === "fund_request") {
          handleNavigation("/");
        } else if (data.notificationType === "support_request") {
          handleNavigation("/");
        } else {
          // Default navigation to notifications page
          handleNavigation("/");
        }
      };

      console.log(`Browser notification sent to ${userId}:`, {
        title,
        body,
        data,
      });
      return true;
    } catch (error) {
      console.error("Error sending browser notification:", error);
      return false;
    }
  };

  // Load previously displayed notification IDs from localStorage on component mount
  useEffect(() => {
    try {
      const savedIds = localStorage.getItem(
        `displayed_notifications_${user?.uid}`
      );
      if (savedIds) {
        setDisplayedNotificationIds(JSON.parse(savedIds));
      }
    } catch (error) {
      console.error(
        "Error loading displayed notification IDs from localStorage:",
        error
      );
    }
  }, [user?.uid]);

  // Save displayed notification IDs to localStorage whenever the list changes
  useEffect(() => {
    if (user?.uid && displayedNotificationIds.length > 0) {
      try {
        localStorage.setItem(
          `displayed_notifications_${user?.uid}`,
          JSON.stringify(displayedNotificationIds)
        );
      } catch (error) {
        console.error(
          "Error saving displayed notification IDs to localStorage:",
          error
        );
      }
    }
  }, [displayedNotificationIds, user?.uid]);

  // Effect to handle countdown timers for supporty requests
  // useEffect(() => {
  //   // Only track unread new_support_request notifications
  //   const activeSupportRequests = supportyRequestNotifications.filter(
  //     (n) => n.trigger === "new_support_request" && !n.read && n.id
  //   );

  //   if (activeSupportRequests.length === 0) {
  //     setSupportRequestTimers({});
  //     return;
  //   }

  //   // Helper to get expiration time
  //   const getExpirationTime = (notification: any) => {
  //     if (notification.meta?.expiration_time) {
  //       return notification.meta.expiration_time;
  //     } else if (notification.meta?.creation_timestamp) {
  //       return notification.meta.creation_timestamp + 60000;
  //     } else if (notification.createdAt) {
  //       return new Date(notification.createdAt).getTime() + 60000;
  //     }
  //     return Date.now();
  //   };

  //   // Set up interval to update timers
  //   const interval = setInterval(() => {
  //     setSupportRequestTimers((prev) => {
  //       const updated: { [id: string]: number } = { ...prev };
  //       activeSupportRequests.forEach((notification) => {
  //         const expiration = getExpirationTime(notification);
  //         const now = Date.now();
  //         const timeLeft = Math.max(0, Math.floor((expiration - now) / 1000));
  //         updated[notification.id] = timeLeft;
  //       });
  //       return updated;
  //     });
  //   }, 1000);

  //   // Initial set
  //   setSupportRequestTimers((prev) => {
  //     const updated: { [id: string]: number } = { ...prev };
  //     activeSupportRequests.forEach((notification) => {
  //       const expiration = getExpirationTime(notification);
  //       const now = Date.now();
  //       const timeLeft = Math.max(0, Math.floor((expiration - now) / 1000));
  //       updated[notification.id] = timeLeft;
  //     });
  //     return updated;
  //   });

  //   return () => clearInterval(interval);
  // }, [supportyRequestNotifications]);

  return (
    <div className="relative" ref={notificationsRef}>
      <button
        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-orange-600 transition"
        onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
      >
        <Bell className="w-5 h-5 text-white" />
        {displayedNotifications.filter((n) => !n.read).length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {displayedNotifications.filter((n) => !n.read).length > 99
              ? "99+"
              : displayedNotifications.filter((n) => !n.read).length}
          </span>
        )}
        {/* Show fund request indicator if there are unread fund requests */}
        {fundRequestNotifications.filter((n) => !n.read).length > 0 && (
          <span className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
            $
          </span>
        )}
        {/* Show supporty request indicator if there are unread supporty requests */}
        {supportyRequestNotifications.filter((n) => !n.read).length > 0 && (
          <span className="absolute -bottom-1 left-0 bg-blue-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
            S
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {showNotificationsDropdown && (
        <div className="absolute right-0 mt-2 w-96 max-h-96 overflow-y-auto bg-white border rounded-lg shadow-lg z-50">
          <div className="p-3 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">Notifications</h3>{" "}
            <button
              className="text-xs text-orange-500 hover:underline"
              onClick={() => {
                if (user?.role === "supporty_admin") {
                  // For admin, mark all global notifications as read
                  dispatch(markAllGlobalNotificationsAsRead());
                } else if (user?.uid) {
                  // For regular users, mark all their notifications as read
                  dispatch(markAllNotificationsAsRead(user.uid));
                }
              }}
            >
              Mark all as read
            </button>
          </div>

          {/* Fund Request Notifications Section */}
          {fundRequestNotifications.length > 0 && (
            <div className="border-b">
              <div
                className="p-2 bg-[#F15D180A] cursor-pointer hover:bg-[#F15D1815]"
                onClick={() => {
                  const fundRequestSection =
                    document.getElementById("fundRequestSection");
                  if (fundRequestSection) {
                    fundRequestSection.classList.toggle("hidden");
                    setFundRequestOpen(!fundRequestOpen);
                  }
                }}
              >
                <h4 className="font-medium text-orange-700 text-sm flex items-center justify-between">
                  <div className="flex items-center">
                    <Wallet className="w-4 h-4 mr-1" />
                    Fund Requests ({fundRequestNotifications.length})
                  </div>
                  {fundRequestOpen ? (
                    <ChevronUp className="h-4 w-4 text-black" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-black" />
                  )}
                </h4>
              </div>
              <div id="fundRequestSection" className="hidden">
                {fundRequestNotifications
                  .slice(
                    0,
                    showAllFundRequests ? fundRequestNotifications.length : 3
                  )
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 hover:bg-gray-50  cursor-pointer border-b ${
                        notification.read ? "bg-white" : "bg-[#0000FF0A]"
                      }`}
                      onClick={() => {
                        // Only navigate, do not mark as read or show accept/reject
                        if (user?.role === "Expert") {
                          navigate("/expert/notification");
                        } else if (user?.role === "Customer") {
                          navigate("/user/notification");
                        } else if (user?.role === "supporty_admin") {
                          navigate("/admin/notification");
                        } else {
                          navigate("/expert/notification");
                        }
                      }}
                    >
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mr-2">
                          <Wallet className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            {!notification.read && (
                              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-800">
                            {notification.message}
                          </p>
                          {(notification.trigger === "fund_request_details" ||
                            notification.trigger ===
                              "fund_request_receive") && (
                            <p className="text-xs text-orange-600 mt-1">
                              Requested by:{" "}
                              {notification.meta?.requesterName ||
                                notification.userEmail ||
                                "Unknown"}
                            </p>
                          )}
                          {notification.trigger === "fund_request_send" && (
                            <p className="text-xs text-blue-600 mt-1">
                              Status: {notification.meta?.status || "pending"}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {notification.createdAt
                              ? new Date(
                                  notification.createdAt
                                ).toLocaleString()
                              : "Just now"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                {fundRequestNotifications.length > 3 && (
                  <div className="p-2 text-center bg-gray-50">
                    <span
                      onClick={() =>
                        setShowAllFundRequests(!showAllFundRequests)
                      }
                      className="text-xs text-orange-500 hover:underline cursor-pointer"
                    >
                      {showAllFundRequests
                        ? "Show less"
                        : "View all fund requests"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Supporty Request Notifications Section */}
          {supportyRequestNotifications.length > 0 && (
            <div className="border-b">
              <div
                className="p-2 bg-[#0000FF0A] cursor-pointer hover:bg-[#0000FF15]"
                onClick={() => {
                  const supportyRequestSection = document.getElementById(
                    "supportyRequestSection"
                  );
                  if (supportyRequestSection) {
                    supportyRequestSection.classList.toggle("hidden");
                    setSupportyRequestOpen(!supportyRequestOpen);
                  }
                }}
              >
                <h4 className="font-medium text-orange-700 text-sm flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    Supporty Requests ({supportyRequestNotifications.length})
                  </div>
                  {supportyRequestOpen ? (
                    <ChevronUp className="h-4 w-4 text-black" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-black" />
                  )}
                </h4>
              </div>
              <div id="supportyRequestSection" className="hidden">
                {supportyRequestNotifications
                  .slice(
                    0,
                    showAllSupportyRequests
                      ? supportyRequestNotifications.length
                      : 3
                  )
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 hover:bg-gray-50  cursor-pointer border-b ${
                        notification.read ? "bg-white" : "bg-[#0000FF0A]"
                      }`}
                      onClick={() => {
                        // Only navigate, do not mark as read or show accept/reject
                        if (user?.role === "Expert") {
                          navigate("/expert/notification");
                        } else if (user?.role === "Customer") {
                          navigate("/user/notification");
                        } else if (user?.role === "supporty_admin") {
                          navigate("/admin/notification");
                        } else {
                          navigate("/expert/notification");
                        }
                      }}
                    >
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mr-2">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            {!notification.read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-800">
                            {notification.message}
                          </p>
                          {notification.meta?.requesterName && (
                            <p className="text-xs text-blue-600 mt-1">
                              From:{" "}
                              {notification.meta.requesterName ||
                                notification.userEmail ||
                                "Unknown"}
                            </p>
                          )}
                          {notification.meta?.status && (
                            <p className="text-xs text-blue-600 mt-1">
                              Status: {notification.meta.status}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {notification.createdAt
                              ? new Date(
                                  notification.createdAt
                                ).toLocaleString()
                              : "Just now"}
                          </p>
                          {/* No Accept/Reject buttons, only navigation on click */}
                        </div>
                      </div>
                    </div>
                  ))}
                {supportyRequestNotifications.length > 3 && (
                  <div className="p-2 text-center bg-gray-50">
                    <span
                      onClick={() =>
                        setShowAllSupportyRequests(!showAllSupportyRequests)
                      }
                      className="text-xs text-blue-500 hover:underline cursor-pointer"
                    >
                      {showAllSupportyRequests
                        ? "Show less"
                        : "View all supporty requests"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Notifications */}
          <div>
            <div className="p-2 bg-gray-50 border-b">
              <h4 className="font-medium text-gray-700 text-sm">
                Recent Notifications
              </h4>
            </div>{" "}
            <div className="divide-y">
              {" "}
              {displayedNotifications.filter(
                (n) =>
                  n.trigger !== "fund_request" &&
                  n.trigger !== "fund_request_details" &&
                  n.trigger !== "fund_request_receive" &&
                  n.trigger !== "fund_request_send" &&
                  n.trigger !== "fund_request_approved" &&
                  n.trigger !== "fund_request_rejected" &&
                  n.trigger !== "funds_added" &&
                  n.trigger !== "new_support_request" &&
                  n.trigger !== "support_request_accepted" &&
                  n.trigger !== "support_request_rejected" &&
                  n.trigger !== "support_request_missed"
              ).length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No other notifications
                </div>
              ) : (
                displayedNotifications
                  .filter(
                    (n) =>
                      n.trigger !== "fund_request" &&
                      n.trigger !== "fund_request_details" &&
                      n.trigger !== "fund_request_receive" &&
                      n.trigger !== "fund_request_send" &&
                      n.trigger !== "fund_request_approved" &&
                      n.trigger !== "fund_request_rejected" &&
                      n.trigger !== "funds_added" &&
                      n.trigger !== "new_support_request" &&
                      n.trigger !== "support_request_accepted" &&
                      n.trigger !== "support_request_rejected" &&
                      n.trigger !== "support_request_missed"
                  )
                  .slice(0, 3)
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 hover:bg-gray-50 ${
                        notification.read ? "bg-white" : "bg-gray-50"
                      }`}
                      onClick={() => {
                        if (
                          !notification.read &&
                          notification.id &&
                          user?.uid
                        ) {
                          if (user?.role === "supporty_admin") {
                            dispatch(
                              markGlobalNotificationAsRead(notification.id)
                            );
                          } else {
                            dispatch(
                              markNotificationAsRead({
                                userId: user.uid,
                                notificationId: notification.id,
                              })
                            );
                          }
                        }
                      }}
                    >
                      <div className="flex items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {notification.createdAt
                              ? new Date(
                                  notification.createdAt
                                ).toLocaleString()
                              : "Just now"}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          {displayedNotifications.length > 6 && (
            <div className="p-2 text-center border-t">
              <span
                onClick={() => handleNavigation("")}
                className="text-sm text-orange-500 hover:underline cursor-pointer"
              >
                View all notifications
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NavbarNotification;
